import { actionChannel, call, fork, put, select, take } from 'redux-saga/effects';
import { buffers, delay } from 'redux-saga';

import { Messages } from '../../codegen/Localize';

import { requestFailed } from '../../app/actions/RequestFailedAction';
import { ServiceLocator } from '../../app/ServiceLocator';

import { MetricDataPoints } from '../resources/MetricDataPoints';
import { IMetricDataList } from '../../ts/ServerApi/types/IMetricDataList';

import { metricsDataRequestFailed, metricsDataRequestStart, metricsDataRequestSucceeded } from '../actions/MetricsDataActions';
import { UpdateMetricsDataAction, UpdateMetricsData } from '../actions/UpdateMetricsActions';
import { getMetricsDataState, notLoadedData, getMetricRefreshList, getMetricsDataPoints } from '../selectors';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { MetricsDataState } from '../state/MetricsDataState';
import { SessionServerSelectors } from '../../app/selectors/SessionInfoSelectors';
import { AppState } from '../../app/state/AppState';
import { RequestHeaders } from '../../httpClient/serverApi/requests/Request';

// The number of Metric data requests to pre-allocate for.
const STARTING_DATA_BUFFER_SIZE = 20;
// The default timeout used between polls for refreshing the metrics if the server session information is not yet available
const DEFAULT_POLLING_DELAY_MS = 5000;
// The mimimum timeout used between polls that can be set by the server
const MINIMUM_POLLING_DELAY_MS = 1000;

// In App, you can dispatch an action whenever you want to display metric data
// There is one `AppState` level object responsible for metric data points, `MetricsDataState`.
// Blocking:
// - Receive request for Metric Data
// - Filter requested ids to only those that are in `NOT_LOADED` state
// - Send Action to update filtered ids to `LOADING` state
// Non-Blocking:
// - Make network request for filtered ids
// - Receive results of network request
// - Send action to update successful ids to `LOADED` with returned data
// - Send action to update unsuccessful ids to `FAILED` with returned error info
export function* metricsDataSaga() {
  yield fork(timerForMetricRefresh);
  const requestChannel = yield actionChannel(UpdateMetricsData, yield buffers.expanding(STARTING_DATA_BUFFER_SIZE));
  while (true) {
    const action = yield take(requestChannel);
    yield call(startMetricDataUpdate, action);
  }
}

export function* timerForMetricRefresh() {
  while (true) {
    if (FeatureFlags.metricsAutoUpdateEnabled && !document.hidden) {
      yield call(startMetricDataAutoRefresh);
    }
    const state = yield select();
    const pollingDelay = getPollingDelay(state);
    yield call(delay, pollingDelay);
  }
}

export function getPollingDelay(state: AppState) {
  const pollingDelayFromServerMS = SessionServerSelectors.metricAutoUpdateTimeDelayMS(state);
  if (pollingDelayFromServerMS) {
    return Math.max(MINIMUM_POLLING_DELAY_MS, pollingDelayFromServerMS);
  }
  else {
    return DEFAULT_POLLING_DELAY_MS;
  }
}

const requestHeadersForAutoRefresh: RequestHeaders = {
  'X-Tableau-Feature': 'auto-refresh-metrics'
};

export function* startMetricDataAutoRefresh() {
  try {
      const state = yield select(getMetricsDataState);
      //only get metrics from the refresh list that are not already loading
      const refreshList = getMetricRefreshList(state);
      if (refreshList.length === 0) {
        return;
      }
      yield put(metricsDataRequestStart(refreshList));
      yield fork(requestDataPointsInBatches, refreshList, requestHeadersForAutoRefresh);
    } catch (e) {
      yield put(requestFailed(e, Messages.errorUnknown()));
    }
}

export function* startMetricDataUpdate(action: UpdateMetricsDataAction) {
  try {
    if (action.metrics.length === 0) {
      return;
    }
    const state = yield select(getMetricsDataPoints);
    const notLoadedIds = notLoadedData(state, action.metrics);
    if (notLoadedIds.length === 0) {
      return;
    }
    yield put(metricsDataRequestStart(notLoadedIds));

    yield fork(requestDataPointsInBatches, notLoadedIds);
    } catch (e) {
      yield put(requestFailed(e, Messages.errorUnknown()));
    }
  }

const METRICS_PAGE_SIZE = 20;

export function* requestDataPointsInBatches(ids: ReadonlyArray<string>, headers?: RequestHeaders) {
  try {
    for (let i = 0; i < ids.length; i += METRICS_PAGE_SIZE) {
      const response = yield call(requestMetricData, ids.slice(i, i + METRICS_PAGE_SIZE), headers);
      if (response.data && !isEmpty(response.data)) {
        yield put(metricsDataRequestSucceeded(response.data));
      }
      if (response.failures && !isEmpty(response.failures)) {
        yield put(metricsDataRequestFailed(response.failures));
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

const isEmpty = (obj: any) => !Object.keys(obj).length;

export async function requestMetricData(ids: string[], headers?: RequestHeaders): Promise<IMetricDataList> {
  return await MetricDataPoints.getMetricDataPoints(ids, headers);
}
